import { useEffect, useRef, useState } from "react";

/**
 * Three-state data hook for InfoPanels. Replaces the per-panel pattern of
 * `useState<T[] | null>` + `setX([])`-on-failure that silently turned a
 * failed fetch into an "empty" state — the user couldn't tell whether
 * "no tasks" meant "really no tasks" or "API call blew up."
 *
 *   - data === null + error === null → still loading
 *   - error !== null                   → render <PanelErrorRow onRetry={reload}/>
 *   - data !== null                    → render normally (empty array is empty)
 *
 * The loader receives an `AbortSignal` so a stale fetch that resolves
 * after the contact switched can short-circuit on the cancelled flag.
 * Deps work like useEffect's; pass primitives (ids, limits) so the
 * effect doesn't refire on every render.
 */
export function usePanelData<T>(
  loader: () => Promise<T>,
  deps: ReadonlyArray<unknown>
): {
  data: T | null;
  error: string | null;
  reload: () => void;
} {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [bump, setBump] = useState(0);
  // Loader stored in a ref so callers don't have to memoize it. The deps
  // array is the source of truth for "should this re-run."
  const loaderRef = useRef(loader);
  loaderRef.current = loader;

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setData(null);
    loaderRef.current()
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : "Failed to load";
        setError(message);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, bump]);

  return {
    data,
    error,
    reload: () => setBump((n) => n + 1),
  };
}
